import { Box, Stack, StackProps, Typography } from '@mui/material';
import { some } from 'lodash';
import { ReactNode, useMemo } from 'react';

import { ProcessingSource } from '@/api/historicalData';
import { LineIcon } from '@/components/data/ColorIcon';
import { minorTextGrey } from '@/theme';

export const CHART_DETAIL_PANEL_WIDTH = '220px';

export type ChartWithInfoProps = {
    chart: ReactNode;
    children: ReactNode;
    chartHeight?: string;
    detailPanelSx?: StackProps['sx'];
    legend?: ReactNode;
};

export const ChartWithInfo = ({
    chart,
    detailPanelSx,
    children,
    chartHeight = '400px',
    legend,
}: ChartWithInfoProps) => {
    return (
        <Stack sx={{ width: 1 }}>
            <Stack direction="row" sx={{ width: 1, position: 'relative' }}>
                <Box sx={{ flexGrow: 1, overflow: 'hidden', height: chartHeight, mt: '-8px', mr: '-24px' }}>
                    {chart}
                </Box>
                <Stack
                    sx={{
                        width: CHART_DETAIL_PANEL_WIDTH,
                        height: '360px',
                        bgcolor: 'rgba(192, 192, 192, 0.2)',
                        boxSizing: 'border-box',
                        ...detailPanelSx,
                    }}
                >
                    {children}
                </Stack>
            </Stack>
            {legend && (
                <Stack direction="row" sx={{ width: 1, alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                    {legend}
                </Stack>
            )}
        </Stack>
    );
};

export const ChartDataSelectors = ({ children, sx, ...props }: StackProps) => {
    return (
        <Stack {...props} sx={{ m: 1, '&:empty': { display: 'none' }, gap: 0.5, ...sx }}>
            {children}
        </Stack>
    );
};

export const ChartDataDetails = ({ children, sx, ...props }: StackProps) => (
    <Stack {...props} sx={{ overflowY: 'auto', ...sx }}>
        {children}
    </Stack>
);

export const LegendItem = ({ icon, children }: { icon: ReactNode; children: ReactNode }) => {
    return (
        <Stack direction="row" sx={{ gap: 0.5, alignItems: 'center' }}>
            {icon}
            <Typography variant="body2" sx={{ color: minorTextGrey }}>
                {children}
            </Typography>
        </Stack>
    );
};

export const ProcessingSourceLegendItems = ({ data }: { data: { processing_source: ProcessingSource }[] }) => {
    const hasBothSources = useMemo(() => {
        const hasHdr = some(data, (d) => d.processing_source === 'hdr');
        const hasEmbedded = some(data, (d) => d.processing_source === 'embedded');

        return hasHdr && hasEmbedded;
    }, [data]);

    if (!hasBothSources) {
        return false;
    }

    return (
        <>
            <LegendItem icon={<LineIcon color="black" dot />}>HDR data</LegendItem>
            <LegendItem icon={<LineIcon color="black" emptyDot />}>Embedded processed data</LegendItem>
        </>
    );
};
